import type {DataRouterFileTreePagePayload, FileOverviewPayload} from '@github-ui/code-view-types'
import {getAppTypeHeader} from '@github-ui/fetch-headers'
import {encodePart} from '@github-ui/paths'
import {type JsonResponse, mainQuery} from '@github-ui/react-core/future/main-query'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'

import {codeViewAppBuilder} from '../config/app-builder'
import {CodeViewPathIsBlobError} from '../pages/CodeViewTreeErrorBoundary'
import {fileTreeQuery} from '../queries/file-tree-query'
import {latestCommitQuery} from '../queries/latest-commit-query'
import {treeCommitInfoQuery} from '../queries/tree-commit-info-query'
import {codeViewBlobRoute} from './code-view-blob-route'

function throwError(json: JsonResponse, routeId: string): never {
  if (json.payload?.[codeViewBlobRoute.id]) {
    throw new CodeViewPathIsBlobError()
  }

  throw new Error(`Unable to find payload for id: ${routeId}`)
}

export const codeViewTreeRoute = codeViewAppBuilder.createQueryRouteConfig('codeViewTreeRoute', {
  path: '/:owner/:repo/tree/:branch/*',
  queries: [
    mainQuery<FileOverviewPayload | DataRouterFileTreePagePayload>({
      queryDeps: ({pathname, searchParams}) => ({
        pathname,
        searchParams: {
          search: searchParams.get('search'),
          files: searchParams.get('files'),
        },
      }),

      queryFn: async ({routeId, queryDeps}) => {
        // Merge the appTypeHeader into any existing headers in queryDeps.init, if present
        const appTypeHeader = getAppTypeHeader('dataRouter')
        const mergedQueryDeps = {
          ...queryDeps,
          pathname: encodePart(queryDeps.pathname),
          init: {
            ...queryDeps?.init,
            headers: {...appTypeHeader, ...queryDeps?.init?.headers},
          },
        }

        const json = await queryFnFetch<JsonResponse>({
          queryDeps: mergedQueryDeps,
        })

        const routePayload = json.payload?.[routeId]
        if (!routePayload) {
          throwError(json, routeId)
        }

        return {
          meta: json.meta,
          payload: routePayload,
        } as unknown as FileOverviewPayload | DataRouterFileTreePagePayload // A select will run internally to extract the payload correctly.
      },
      encodeFetchPath: true,
    }),
    fileTreeQuery,
    latestCommitQuery,
    treeCommitInfoQuery,
  ],
})
